import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectClipEventIds } from "store/clip/selectors";
import { removeEventId } from "store/clip/slice";
import { selectClipVideoEventIds } from "store/clipVideosList/selectors";
import { removeClipVideoEventId } from "store/clipVideosList/slice";

import { SocketContext } from "../../../../context/socket";
import { showNotification } from "../../notification/notification";

const WebSocketZipURL = ({
  clipEventIds,
  removeEventId,
  clipVideoEventIds,
  removeClipVideoEventId,
  isForClipVideo = false,
}) => {
  const { webhookSocket } = useContext(SocketContext);
  // Add a ref to track if we're currently downloading
  const isDownloading = useRef(false);

  const eventIds = useMemo(() => {
    return isForClipVideo ? clipVideoEventIds : clipEventIds;
  }, [clipEventIds, clipVideoEventIds, isForClipVideo]);

  const removeEventIdFunc = useMemo(() => {
    return isForClipVideo ? removeClipVideoEventId : removeEventId;
  }, [removeEventId, removeClipVideoEventId, isForClipVideo]);

  const handleDownload = useCallback((zipURL, event) => {
    if (isDownloading.current) {
      // If already downloading, wait a bit
      setTimeout(() => handleDownload(zipURL, event), 2000);

      return;
    }

    isDownloading.current = true;
    showNotification({ message: "Zip has been downloaded" });

    const link = document.createElement("a");

    link.href = zipURL;
    link.download = event;
    link.rel = "noopener noreferrer";

    // Append to DOM
    document.body.appendChild(link);
    link.click();

    // Clean up after a short delay
    setTimeout(() => {
      document.body.removeChild(link);
      // Allow next download after 2 seconds
      setTimeout(() => {
        isDownloading.current = false;
      }, 2000);
    }, 500);
  }, []);

  useEffect(() => {
    if (eventIds.length > 0) {
      eventIds.forEach((eventId) => {
        webhookSocket.on(`publisher_zip-url-${eventId}`, ({ data }) => {
          if (data) {
            removeEventIdFunc(eventId);
            const { payload } = data;
            if (payload) {
              const { event, zipURL } = payload;

              handleDownload(zipURL, event);
            }
          }
        });
      });
    }

    return () => {
      eventIds.forEach((eventId) => {
        webhookSocket.off(`publisher_zip-url-${eventId}`);
      });
    };
  }, [eventIds, removeEventIdFunc, webhookSocket, handleDownload]);

  return null;
};

const mapStateToProps = createStructuredSelector({
  clipEventIds: selectClipEventIds,
  clipVideoEventIds: selectClipVideoEventIds,
});

const mapDispatchToProps = {
  removeEventId,
  removeClipVideoEventId,
};

export default connect(mapStateToProps, mapDispatchToProps)(WebSocketZipURL);
